/**
 * PhotoGallery Component
 * 
 * Displays all captured photos in a scrollable grid layout.
 * Each photo is rendered as a DraggablePhoto component that can be dragged to layout slots.
 * Shows total photo count and provides vertical scrolling for large collections.
 * 
 * Requirements: 4.1, 4.2, 4.3, 4.4, 4.5
 */

import { DraggablePhoto } from './DraggablePhoto';
import type { EditPhotoMinimal } from '../types/edit-session';

export interface PhotoGalleryProps {
  /** Array of photos to display in the gallery */
  photos: EditPhotoMinimal[];
  /** Map of slot indices to photo IDs showing which photos are placed in the layout */
  photoArrangement: Map<number, string>;
  /** Optional callback when drag starts */
  onDragStart?: (photoId: string) => void;
  /** Optional callback when drag ends */
  onDragEnd?: (photoId: string, didDrop: boolean) => void;
}

/**
 * PhotoGallery component - Right panel displaying all captured photos
 */
export function PhotoGallery({ 
  photos, 
  photoArrangement,
  onDragStart,
  onDragEnd,
}: PhotoGalleryProps) {
  // Sort photos by sequence number for consistent display
  const sortedPhotos = [...photos].sort((a, b) => a.sequence - b.sequence);

  // Create a Set of placed photo IDs for quick lookup
  const placedPhotoIds = new Set(photoArrangement.values());

  return (
    <div
      className="flex flex-col h-full min-h-0 bg-white rounded-lg shadow-md p-4"
      data-testid="photo-gallery"
      role="region"
      aria-labelledby="photo-gallery-title"
    >
      {/* Header with photo count */}
      <div className="flex-shrink-0 flex items-center justify-between mb-4">
        <h2 id="photo-gallery-title" className="text-lg font-semibold text-gray-800">
          Gallery
        </h2>
        <span
          className="text-sm text-gray-600 bg-gray-100 px-3 py-1 rounded-full"
          data-testid="photo-count"
          aria-live="polite"
        >
          {photos.length} {photos.length === 1 ? 'photo' : 'photos'}
        </span>
      </div>

      {/* Scrollable photo grid */}
      <div
        className="flex-1 min-h-0 overflow-y-auto"
        data-testid="photo-gallery-scroll-container"
      >
        {photos.length === 0 ? (
          // Empty state
          <div
            className="flex flex-col items-center justify-center h-full text-gray-400"
            data-testid="photo-gallery-empty"
            role="status"
          >
            <svg
              className="w-16 h-16 mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <p className="text-center">No photos available</p>
          </div>
        ) : (
          // Photo grid
          <div
            className="grid grid-cols-2 gap-4"
            data-testid="photo-gallery-grid"
          >
            {sortedPhotos.map((photo) => (
              <DraggablePhoto
                key={photo.photoId}
                photo={photo}
                isPlaced={placedPhotoIds.has(photo.photoId)}
                onDragStart={onDragStart}
                onDragEnd={onDragEnd}
              />
            ))}
          </div>
        )}
      </div>

      {/* Helper text */}
      {photos.length > 0 && (
        <div className="flex-shrink-0 mt-4 pt-4 border-t border-gray-200">
          <p className="text-xs text-gray-500 text-center">
            Drag photos to the layout preview to arrange them
          </p>
        </div>
      )}
    </div>
  );
}
